import {
  ChakraAccordion,
  ChakraAccordionButton,
  ChakraAccordionIcon,
  ChakraAccordionItem,
  ChakraAccordionPanel,
  ChakraBox,
  ChakraText,
} from '@equip.health/ui';

import {
  APPOINTMENT_STATUS,
  AppointmentViewDetail,
  PatientProviderDashboard,
  VIEW_DETAIL_TYPE,
} from '~/store/services/providerDashboard';
import AppointmentCard from './AppointmentCard';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { ProviderDashboardEvents } from '~/analytics/providerDashboardEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

interface TodaysSessionsListProps {
  appointments: PatientProviderDashboard[];
  canJoin: (appointment: AppointmentViewDetail) => boolean;
  onJoin: (appointmentExternalId: string) => void;
  onOpenNote: (appointment: AppointmentViewDetail, patientId: string) => void;
}

const appointmentOf = (
  patient: PatientProviderDashboard,
): AppointmentViewDetail | null =>
  patient.viewDetail?.type === VIEW_DETAIL_TYPE.APPOINTMENT
    ? patient.viewDetail
    : null;

const isUpcoming = (patient: PatientProviderDashboard): boolean =>
  appointmentOf(patient)?.status === APPOINTMENT_STATUS.UPCOMING;

const TodaysSessionsList = ({
  appointments,
  canJoin,
  onJoin,
  onOpenNote,
}: TodaysSessionsListProps) => {
  const { trackEvent } = useAnalytics() ?? {};

  if (!appointments.length) return null;

  const upcoming = appointments.filter(isUpcoming);
  const past = appointments.filter((patient) => !isUpcoming(patient));

  const renderCard = (patient: PatientProviderDashboard, isPast: boolean) => {
    const appointment = appointmentOf(patient);
    if (!appointment) return null;
    const isCancelled =
      appointment.status === APPOINTMENT_STATUS.CANCELLED_BY_PATIENT;
    return (
      <AppointmentCard
        key={appointment.appointmentExternalId}
        patient={patient}
        appointment={appointment}
        isPast={isPast}
        showJoin={!isPast && canJoin(appointment)}
        onJoin={onJoin}
        onOpenNote={
          isCancelled
            ? undefined
            : () => onOpenNote(appointment, patient.patientExternalId)
        }
      />
    );
  };

  return (
    <ChakraBox>
      {upcoming.map((patient) => renderCard(patient, false))}

      {past.length > 0 && (
        <ChakraAccordion allowToggle width="100%">
          <ChakraAccordionItem border="none">
            {({ isExpanded }: { isExpanded: boolean }) => (
              <>
                <ChakraAccordionButton
                  paddingX="0"
                  paddingY="12px"
                  _hover={{ bg: 'transparent' }}
                  data-testid="todays-sessions-past-toggle"
                  onClick={() => {
                    trackEvent?.(
                      ProviderDashboardEvents.myPatientsPastSessionsToggled,
                      {
                        isExpanded: !isExpanded,
                        pastAppointmentCount: past.length,
                        Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
                      },
                    );
                  }}
                >
                  <ChakraAccordionIcon marginRight="6px" />
                  <ChakraText
                    fontSize="13px"
                    fontWeight="500"
                    color="neutral.primary"
                  >
                    {isExpanded ? 'Hide' : 'Show'} {past.length} past
                    appointment
                    {past.length === 1 ? '' : 's'}
                  </ChakraText>
                </ChakraAccordionButton>
                <ChakraAccordionPanel padding="0">
                  {past.map((patient) => renderCard(patient, true))}
                </ChakraAccordionPanel>
              </>
            )}
          </ChakraAccordionItem>
        </ChakraAccordion>
      )}
    </ChakraBox>
  );
};

export default TodaysSessionsList;
